import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

const PURPOSE_OPTIONS = [
    { value: 'mixer', label: 'Mixer' },
    { value: 'pump', label: 'Pump' },
    { value: 'dg_set', label: 'DG Set' },
    { value: 'tipper', label: 'Tipper' },
    { value: 'loader', label: 'Loader' },
    { value: 'generator', label: 'Generator' },
    { value: 'other', label: 'Other' },
];

function resolvePurposeFromEquipment(equipment) {
    if (!equipment) return '';
    const typeName = String(
        (typeof equipment.equip_type === 'string'
            ? equipment.equip_type
            : equipment.equip_type?.name)
        || equipment.equip_grp_name
        || equipment.equip_grp_code?.name
        || equipment.equip_grp_code?.entity_name
        || ''
    ).toUpperCase().trim();

    if (!typeName) return 'other';
    if (typeName.includes('MIXER') || typeName.includes('TRANSIT')) return 'mixer';
    if (typeName.includes('PUMP')) return 'pump';
    if (typeName.includes('DG') || typeName.includes('D.G')) return 'dg_set';
    if (typeName.includes('TIPPER')) return 'tipper';
    if (typeName.includes('LOADER')) return 'loader';
    if (typeName.includes('GENERATOR')) return 'generator';
    return 'other';
}

function resolveOpeningMeterFromEquipment(equipment) {
    if (!equipment) return 0;
    const meterReading = Number(equipment.meter_reading);
    if (meterReading > 0) return meterReading;
    const hourMeter = Number(equipment.equip_open_hm || equipment.co_open_hm);
    if (hourMeter > 0) return hourMeter;
    return 0;
}

function equipmentMatchesPurpose(equipment, purpose) {
    if (!purpose) return false;
    return resolvePurposeFromEquipment(equipment) === purpose;
}

function calcKmHmReading(row) {
    const opening = Number(row.opening_meter) || 0;
    const closing = Number(row.closing_meter) || 0;
    const meterRun = closing - opening;
    if (meterRun <= 0) return '';
    return meterRun.toFixed(3);
}

function calcAvgKmHmPerLtr(row) {
    const dieselQty = Number(row.consumed_qty) || 0;
    const meterRun = Number(calcKmHmReading(row));
    if (dieselQty <= 0 || !(meterRun > 0)) return '';
    return (meterRun / dieselQty).toFixed(3);
}

function resolveAvailableStock(productId, rowIndex, rowsData, stockByProduct, stockAdjustByProduct) {
    if (!productId) return null;
    const key = String(productId);
    const closing = Number(
        stockByProduct?.[key]?.closing_qty
        ?? stockByProduct?.[productId]?.closing_qty
        ?? 0,
    );
    const adjust = Number(
        stockAdjustByProduct?.[key]
        ?? stockAdjustByProduct?.[productId]
        ?? 0,
    );
    const usedElsewhere = (rowsData || []).reduce((sum, row, i) => {
        if (i === rowIndex) return sum;
        if (String(row.product_id) !== key) return sum;
        return sum + (Number(row.consumed_qty) || 0);
    }, 0);
    return Number((closing + adjust - usedElsewhere).toFixed(3));
}

function TableRowsDieselConsumption({
    rowsData,
    equipments,
    dieselProducts,
    driverOptions = [],
    stockByProduct = {},
    stockAdjustByProduct = {},
    plantSelected = false,
    onChange,
    onAdd,
    onRemove,
    onCreateDriver,
    readOnly = false,
}) {
    const [stockHint, setStockHint] = useState(null);
    const hideHintTimer = useRef(null);

    useEffect(() => () => {
        if (hideHintTimer.current) clearTimeout(hideHintTimer.current);
    }, []);

    const showStockAvailability = (index, row, qtyValue) => {
        if (readOnly) return;
        let text = '';
        let tone = 'info';

        if (!plantSelected) {
            text = 'Select plant to check stock';
            tone = 'warn';
        } else if (!row.product_id) {
            text = 'Select diesel product to check stock';
            tone = 'warn';
        } else {
            const available = resolveAvailableStock(
                row.product_id,
                index,
                rowsData,
                stockByProduct,
                stockAdjustByProduct,
            );
            const unit = row.unit || 'Ltr';
            const entered = Number(qtyValue) || 0;
            if (available === null) {
                text = 'Select diesel product to check stock';
                tone = 'warn';
            } else if (entered > available) {
                text = `Available stock: ${available.toFixed(3)} ${unit} (short by ${(entered - available).toFixed(3)})`;
                tone = 'danger';
            } else {
                text = `Available stock: ${available.toFixed(3)} ${unit}`;
                tone = 'ok';
            }
        }

        setStockHint({ index, text, tone });
        if (hideHintTimer.current) clearTimeout(hideHintTimer.current);
        hideHintTimer.current = setTimeout(() => setStockHint(null), 2800);
    };
    const allEquipmentOptions = (equipments || []).map((e) => ({
        value: e.id,
        label: e.equip_name || e.name,
        equipment: e,
        purpose: resolvePurposeFromEquipment(e),
    }));

    const getEquipmentOptionsForPurpose = (purpose) => {
        if (!purpose) return [];
        return allEquipmentOptions.filter((opt) => opt.purpose === purpose);
    };

    const productOptions = (dieselProducts || []).map((p) => ({
        value: p.id,
        label: p.name,
        unit: p.unit?.name || p.unit_name || 'Ltr',
    }));

    const handlePurposeChange = (index, purpose) => {
        const updated = [...rowsData];
        const current = updated[index] || {};
        const equipmentStillValid = current.equipment_id
            && equipmentMatchesPurpose(
                (equipments || []).find((e) => e.id === current.equipment_id),
                purpose,
            );

        updated[index] = {
            ...current,
            purpose,
            equipment_id: equipmentStillValid ? current.equipment_id : '',
            equipment_name: equipmentStillValid ? current.equipment_name : '',
            opening_meter: equipmentStillValid ? current.opening_meter : 0,
        };
        onChange(updated);
    };

    const handleEquipmentSelect = (index, option) => {
        const equipment = option?.equipment
            || (equipments || []).find((e) => e.id === option?.value);
        const updated = [...rowsData];
        updated[index] = {
            ...updated[index],
            equipment_id: option?.value || '',
            equipment_name: option?.label || '',
            opening_meter: equipment ? resolveOpeningMeterFromEquipment(equipment) : 0,
        };
        onChange(updated);
    };

    const handleProductSelect = (index, option) => {
        const updated = [...rowsData];
        updated[index] = {
            ...updated[index],
            product_id: option?.value || '',
            product_name: option?.label || '',
            unit: option?.unit || updated[index].unit || 'Ltr',
        };
        onChange(updated);
    };

    const handleFieldChange = (index, name, value) => {
        const updated = [...rowsData];
        updated[index] = { ...updated[index], [name]: value };

        if (name === 'opening_meter' || name === 'closing_meter') {
            const opening = parseFloat(updated[index].opening_meter) || 0;
            const closing = parseFloat(updated[index].closing_meter) || 0;
            if (closing > opening) {
                updated[index].consumed_qty = parseFloat((closing - opening).toFixed(3));
                showStockAvailability(index, updated[index], updated[index].consumed_qty);
            }
        }

        if (name === 'consumed_qty') {
            showStockAvailability(index, updated[index], value);
        }

        onChange(updated);
    };

    return (
        <>
            {rowsData.map((row, index) => {
                const equipmentOptions = getEquipmentOptionsForPurpose(row.purpose);
                const selectedEquipment = equipmentOptions.find((o) => o.value === row.equipment_id)
                    || (row.equipment_id
                        ? allEquipmentOptions.find((o) => o.value === row.equipment_id) || null
                        : null);

                return (
                <tr key={row.line_no || index} className="text-center detailclass">
                    {!readOnly && (
                        <td className="text-nowrap">
                            {index === 0 && onAdd && (
                                <button
                                    type="button"
                                    className="btn btn-success btn-sm mr-1"
                                    title="Add Line"
                                    onClick={onAdd}
                                >
                                    +
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                title="Remove Line"
                                onClick={() => onRemove(index)}
                                disabled={rowsData.length <= 1}
                            >
                                ×
                            </button>
                        </td>
                    )}
                    <td>{index + 1}</td>
                    <td style={{ minWidth: '160px' }}>
                        {readOnly ? (
                            PURPOSE_OPTIONS.find((o) => o.value === row.purpose)?.label || row.purpose || '-'
                        ) : (
                            <select
                                className="form-control form-control-sm"
                                value={row.purpose || ''}
                                onChange={(e) => handlePurposeChange(index, e.target.value)}
                                required
                            >
                                <option value="">Select</option>
                                {PURPOSE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        )}
                    </td>
                    <td style={{ minWidth: '260px' }}>
                        {readOnly ? (
                            row.equipment_name || '-'
                        ) : (
                            <Select
                                options={equipmentOptions}
                                value={selectedEquipment}
                                onChange={(opt) => handleEquipmentSelect(index, opt)}
                                placeholder={row.purpose ? 'Select equipment' : 'Select purpose first'}
                                isDisabled={!row.purpose}
                                isClearable
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                noOptionsMessage={() => (
                                    row.purpose
                                        ? `No equipment found for ${PURPOSE_OPTIONS.find((o) => o.value === row.purpose)?.label || row.purpose}`
                                        : 'Select purpose first'
                                )}
                            />
                        )}
                    </td>
                    <td style={{ minWidth: '180px' }}>
                        {readOnly ? row.driver_name : (
                            <CreatableSelect
                                options={driverOptions}
                                value={
                                    row.driver_name
                                        ? (driverOptions.find((o) => o.value === row.driver_name)
                                            || { value: row.driver_name, label: row.driver_name })
                                        : null
                                }
                                onChange={(opt) => handleFieldChange(index, 'driver_name', opt?.value || '')}
                                onCreateOption={(inputValue) => {
                                    const name = String(inputValue || '').trim();
                                    if (!name) return;
                                    if (onCreateDriver) onCreateDriver(name);
                                    handleFieldChange(index, 'driver_name', name);
                                }}
                                placeholder="Select or create"
                                isClearable
                                isSearchable
                                formatCreateLabel={(input) => `Create "${input}"`}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                        )}
                    </td>
                    <td style={{ minWidth: '220px' }}>
                        {readOnly ? (
                            row.product_name
                        ) : (
                            <Select
                                options={productOptions}
                                value={productOptions.find((o) => o.value === row.product_id) || null}
                                onChange={(opt) => handleProductSelect(index, opt)}
                                placeholder="Select diesel"
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                        )}
                    </td>
                    <td style={{ minWidth: '130px' }}>
                        {readOnly ? Number(row.opening_meter || 0).toFixed(3) : (
                            <input
                                type="number"
                                step="0.001"
                                className="form-control form-control-sm"
                                style={{ textAlign: 'right' }}
                                value={row.opening_meter || 0}
                                onChange={(e) => handleFieldChange(index, 'opening_meter', e.target.value)}
                            />
                        )}
                    </td>
                    <td style={{ minWidth: '130px' }}>
                        {readOnly ? Number(row.closing_meter || 0).toFixed(3) : (
                            <input
                                type="number"
                                step="0.001"
                                className="form-control form-control-sm"
                                style={{ textAlign: 'right' }}
                                value={row.closing_meter || 0}
                                onChange={(e) => handleFieldChange(index, 'closing_meter', e.target.value)}
                            />
                        )}
                    </td>
                    <td style={{ minWidth: '130px', position: 'relative' }}>
                        {readOnly ? Number(row.consumed_qty || 0).toFixed(3) : (
                            <>
                                <input
                                    type="number"
                                    step="0.001"
                                    min="0"
                                    required
                                    className="form-control form-control-sm"
                                    style={{ textAlign: 'right' }}
                                    value={row.consumed_qty || 0}
                                    onChange={(e) => handleFieldChange(index, 'consumed_qty', e.target.value)}
                                    onFocus={() => showStockAvailability(index, row, row.consumed_qty)}
                                />
                                {stockHint?.index === index && (
                                    <div
                                        className={`diesel-stock-hint diesel-stock-hint--${stockHint.tone}`}
                                        role="status"
                                    >
                                        {stockHint.text}
                                    </div>
                                )}
                            </>
                        )}
                    </td>
                    <td style={{ minWidth: '80px' }}>{row.unit || 'Ltr'}</td>
                    <td style={{ minWidth: '120px', textAlign: 'right', fontWeight: 'bold' }}>
                        {calcKmHmReading(row) || '-'}
                    </td>
                    <td style={{ minWidth: '120px', textAlign: 'right', fontWeight: 'bold' }}>
                        {calcAvgKmHmPerLtr(row) || '-'}
                    </td>
                </tr>
                );
            })}
        </>
    );
}

export default TableRowsDieselConsumption;
